import {Schema} from "effect";

// ── Anonymous UID type hierarchy ───────────────────────────────────────
//
//   AnonymousUid (`anon_${string}`)     — a specific anonymous session token
//   AnyAnonymousUid (`any-anon_${string}`) — wildcard / nil anonymous identity
//   SomeAnonymousUid = AnonymousUid | AnyAnonymousUid
//
// Both are structurally disjoint from PrefixedUserUid (`usr_${string}`),
// making it impossible to accidentally pass an anonymous identifier where
// a named user is expected and vice-versa.
//
// AnonymousUid tags a concrete throwaway session (many values, one per
// session). AnyAnonymousUid is the "any" / top type for the anonymous
// domain — it means "some anonymous user, identity unspecified." The
// canonical value is NIL_ANONYMOUS_UID, the prefixed form of the legacy
// ANON_ROLE_UID used in permission rules and Firestore security rules.
//
// The prefix does NOT encode which auth system issued the token. Named
// users use a single `usr_` prefix regardless of whether the UID
// originated in Firebase Auth or Flux auth; anonymous UIDs follow the
// same convention with a single `anon_` prefix.

const ANON_PREFIX_RE = /^(?:anon_|any-anon_)/;
function stripAnonPrefix(uid: string): string {
    return uid.replace(ANON_PREFIX_RE, "");
}

// ── AnonymousUid (specific anonymous session) ──────────────────────────

const AnonymousUidSchema = Schema.TemplateLiteral("anon_", Schema.String).pipe(Schema.brand("AnonymousUid"));

export type AnonymousUid = typeof AnonymousUidSchema.Type;

export const isAnonymousUid = Schema.is(AnonymousUidSchema);

export function toAnonymousUid(uid: string): AnonymousUid {
    return (isAnonymousUid(uid) ? uid : `anon_${stripAnonPrefix(uid)}`) as AnonymousUid;
}

export function parseAnonymousUid(uid: string): string {
    return uid.startsWith("anon_") ? uid.slice("anon_".length) : uid;
}

// eslint-disable-next-line @typescript-eslint/no-redeclare
export const AnonymousUid = {
    schema: AnonymousUidSchema,
    isAnonymousUid,
    toAnonymousUid,
    parseAnonymousUid,
} as const;

// ── AnyAnonymousUid (wildcard / nil anonymous identity) ────────────────

const AnyAnonymousUidSchema = Schema.TemplateLiteral("any-anon_", Schema.String).pipe(Schema.brand("AnyAnonymousUid"));

export type AnyAnonymousUid = typeof AnyAnonymousUidSchema.Type;

export const isAnyAnonymousUid = Schema.is(AnyAnonymousUidSchema);

export function toAnyAnonymousUid(uid: string): AnyAnonymousUid {
    return (isAnyAnonymousUid(uid) ? uid : `any-anon_${stripAnonPrefix(uid)}`) as AnyAnonymousUid;
}

export function parseAnyAnonymousUid(uid: string): string {
    return uid.startsWith("any-anon_") ? uid.slice("any-anon_".length) : uid;
}

/**
 * The canonical nil-user identifier, replacing the legacy bare-UUID
 * `ANON_ROLE_UID` constant.
 */
export const NIL_ANONYMOUS_UID = "any-anon_0338f920-f103-45be-9462-2cae879caa37" as AnyAnonymousUid;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export const AnyAnonymousUid = {
    schema: AnyAnonymousUidSchema,
    isAnyAnonymousUid,
    toAnyAnonymousUid,
    parseAnyAnonymousUid,
    NIL_ANONYMOUS_UID,
} as const;

// ── Union type ─────────────────────────────────────────────────────────

export type SomeAnonymousUid = AnonymousUid | AnyAnonymousUid;

export function isSomeAnonymousUid(uid: unknown): uid is SomeAnonymousUid {
    return isAnonymousUid(uid) || isAnyAnonymousUid(uid);
}
